import { Autocomplete, AutocompleteItem } from '@/components/ui';
import type { Key } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useGroups } from '@/store/people/hooks';
import type { Group } from '@/shared/types/group';

export interface SheepfoldAutocompleteItem {
  id: string;
  label: string;
  /** Section title when this is a group (laygroup2) */
  sectionLabel?: string;
  type: 'laygroup1' | 'laygroup2';
}

export interface SheepfoldAutocompleteProps {
  /** Selected group/section id, or empty string for none */
  value: string;
  /** Called when selection changes (id or '') */
  onChange: (id: string) => void;
  placeholder?: string;
  label?: React.ReactNode;
  className?: string;
  'aria-label'?: string;
  disabled?: boolean;
  /** When true, show a "Clear selection" link when a value is set. Default true. */
  allowClear?: boolean;
}

/**
 * Build flat list of all Sheepfold Sections (laygroup1) and Sheepfold Groups
 * (laygroup2, with optional section label), sorted alphabetically by their
 * display label.
 */
function buildSheepfoldItems(groups: Group[]): SheepfoldAutocompleteItem[] {
  const sections = groups
    .filter((g) => g.data.type === 'laygroup1')
    .sort((a, b) =>
      (a.data.title || '').localeCompare(b.data.title || '', undefined, {
        sensitivity: 'base',
      }),
    );
  const groupsL2 = groups.filter((g) => g.data.type === 'laygroup2');
  const byParent = new Map<string, Group[]>();
  for (const g of groupsL2) {
    const pid = g.data.parent_id || '';
    if (!byParent.has(pid)) {
      byParent.set(pid, []);
    }
    byParent.get(pid)!.push(g);
  }
  const result: SheepfoldAutocompleteItem[] = [];
  for (const s of sections) {
    result.push({
      id: s.id,
      label: s.data.title || s.id,
      type: 'laygroup1',
    });
    const children = byParent.get(s.id) || [];
    children.sort((a, b) =>
      (a.data.title || '').localeCompare(b.data.title || '', undefined, {
        sensitivity: 'base',
      }),
    );
    for (const c of children) {
      result.push({
        id: c.id,
        label: c.data.title || c.id,
        sectionLabel: s.data.title || undefined,
        type: 'laygroup2',
      });
    }
  }
  // Sort final flat list alphabetically by the same label shown in the UI
  result.sort((a, b) => {
    const labelA = a.sectionLabel ? `${a.label} (${a.sectionLabel})` : a.label;
    const labelB = b.sectionLabel ? `${b.label} (${b.sectionLabel})` : b.label;
    return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
  });
  return result;
}

/**
 * Sheepfold filter autocomplete: choose a Sheepfold Section or any Sheepfold Group.
 * Uses groups from store (laygroup1 = section, laygroup2 = group).
 */
export function SheepfoldAutocomplete({
  value,
  onChange,
  placeholder = 'All sheepfold',
  label,
  className,
  'aria-label': ariaLabel,
  disabled,
  allowClear = true,
}: SheepfoldAutocompleteProps) {
  const { list } = useGroups(undefined, undefined, undefined);
  const items = useMemo(() => buildSheepfoldItems(list), [list]);

  const selectedItem = value ? items.find((i) => i.id === value) : null;
  let selectedLabel = '';
  if (selectedItem) {
    selectedLabel = selectedItem.sectionLabel
      ? `${selectedItem.label} (${selectedItem.sectionLabel})`
      : selectedItem.label;
  }

  const [inputValue, setInputValue] = useState(selectedLabel);

  useEffect(() => {
    setInputValue(selectedLabel);
  }, [selectedLabel]);

  const handleSelectionChange = (key: Key | null) => {
    const id = key ? String(key) : '';
    const item = id ? items.find((i) => i.id === id) : null;
    let display = '';
    if (item) {
      display = item.sectionLabel
        ? `${item.label} (${item.sectionLabel})`
        : item.label;
    }
    setInputValue(display);
    onChange(id);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
  };

  return (
    <div className={className}>
      <Autocomplete
        label={label}
        placeholder={placeholder}
        items={items}
        selectedKey={value || null}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSelectionChange={handleSelectionChange}
        disabled={disabled}
        aria-label={ariaLabel ?? 'Select sheepfold section or group'}
        listboxProps={{
          emptyContent:
            inputValue.trim() === ''
              ? 'Type to search section or group'
              : 'No matching section or group',
          portal: true,
        }}
      >
        {(item) => (
          <AutocompleteItem
            key={item.id}
            textValue={
              item.sectionLabel
                ? `${item.label} (${item.sectionLabel})`
                : item.label
            }
          >
            <span className='font-medium'>{item.label}</span>
            {item.sectionLabel && (
              <span className='ml-1.5 text-xs text-slate-500'>
                — {item.sectionLabel}
              </span>
            )}
          </AutocompleteItem>
        )}
      </Autocomplete>
      {allowClear && value && (
        <button
          type='button'
          className='mt-1.5 text-xs text-primary hover:underline'
          onClick={handleClear}
        >
          Clear selection
        </button>
      )}
    </div>
  );
}
